/** @jsx jsx */
import React, { useCallback } from 'react';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form, { FormFooter, FormSection, Field, useFormState } from '@atlaskit/form';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import Select, { type ValueType } from '@atlaskit/select';
import Link from '@atlaskit/link';
import Spinner from '@atlaskit/spinner';
import { useRouterActions } from '@atlassian/react-resource-router';
import type {
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
	ApplicationLinkV3Status,
	RestApplinkOAuthStatus,
	RestOAuthConfig,
} from '../../../common/entities.tsx';
import { ApplicationUrl, DisplayName, DisplayUrl } from '../../common/fields/index.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../../common/constants.tsx';
import { putApplinkOAuth1Config, updateApplinkV3 } from '../../../rest/index.tsx';
import { lookupTypeFromV3Data, type TypeDefinition } from '../../../common/application-types.tsx';
import { getApplinksAdminUrl } from '../../../common/interchange.tsx';
import type { SelectOption } from '../../../common/types.tsx';
import { ArrowDescription, Arrows, type ArrowState } from './constants.tsx';

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
	fieldNegativeMargin: {
		// Workaround for https://product-fabric.atlassian.net/browse/DSP-5423
		marginTop: token('space.negative.100'),
	},
	colDirection: {
		width: '20%',
	},
	colOutgoing: {
		width: '40%',
	},
	colIncoming: {
		width: '40%',
	},
});

type AuthOption = SelectOption;

const AUTH_OAUTH1_IMPERSONATION: AuthOption = {
	label: 'OAuth (impersonation)',
	value: 'oauthWithImpersonation',
};
const AUTH_OAUTH1: AuthOption = { label: 'OAuth', value: 'oauth' };
const AUTH_DISABLED: AuthOption = { label: 'Disabled', value: 'disabled' };
const AUTH_UNKNOWN: AuthOption = { label: 'Unknown', value: 'unknown' };

// This one should never be shown to the user (as this page is for OAuth 1.0), but
// in the event it is, provides a 'more correct' value for troubleshooting.
const AUTH_OAUTH2: AuthOption = { label: 'OAuth 2.0', value: 'oauth2ThreeLo' };

const AUTH_TYPES: AuthOption[] = [AUTH_OAUTH1, AUTH_OAUTH1_IMPERSONATION, AUTH_DISABLED];

const getAuthLevel = (config?: RestOAuthConfig): AuthOption => {
	if (!config) {
		return AUTH_UNKNOWN;
	}
	if (config.enabled && config.twoLoEnabled && config.twoLoImpersonationEnabled) {
		return AUTH_OAUTH1_IMPERSONATION;
	}
	if (config.enabled && !config.twoLoImpersonationEnabled) {
		return AUTH_OAUTH1;
	}
	if (config.oAuth2ThreeLoEnabled) {
		return AUTH_OAUTH2;
	}
	if (
		!config.enabled &&
		!config.twoLoEnabled &&
		!config.twoLoImpersonationEnabled &&
		!config.oAuth2ThreeLoEnabled
	) {
		return AUTH_DISABLED;
	}
	return AUTH_UNKNOWN;
};

const matchOAuthConfigs = (
	localAuthLevel?: ValueType<AuthOption>,
	remoteAuthLevel?: ValueType<AuthOption>,
): ArrowState => {
	if (!localAuthLevel || !remoteAuthLevel) {
		return 'mismatch';
	}

	if (localAuthLevel.value === remoteAuthLevel.value) {
		if (localAuthLevel.value === AUTH_DISABLED.value) {
			return 'disabled';
		}
		return 'match';
	}
	return 'mismatch';
};

const ApplinksConnectedArrow = (props: { isIncoming: boolean; state: ArrowState }) => {
	// Render the appropriate arrow icon based on the connection state
	return props.isIncoming ? (
		<img
			height="16px"
			src={Arrows.incoming[props.state]}
			alt={ArrowDescription.incoming[props.state]}
		/>
	) : (
		<img
			height="16px"
			src={Arrows.outgoing[props.state]}
			alt={ArrowDescription.outgoing[props.state]}
		/>
	);
};

const ApplinksAuthSelect = (props: { name: string; defaultValue: SelectOption }): JSX.Element => {
	return (
		<Box xcss={styles.fieldNegativeMargin}>
			<Field<ValueType<SelectOption>> name={props.name} defaultValue={props.defaultValue}>
				{({ fieldProps }) => <Select {...fieldProps} inputId={props.name} options={AUTH_TYPES} />}
			</Field>
		</Box>
	);
};

const LocalAuthDetails = (props: {
	isIncoming: boolean;
	state: ArrowState;
	name: string;
	defaultOAuthConfig?: RestOAuthConfig;
}) => {
	const getAuthOptionFromConfig = useCallback(() => {
		return getAuthLevel(props.defaultOAuthConfig);
	}, [props.defaultOAuthConfig]);

	return (
		<Inline space="space.200" alignBlock="center">
			<Box xcss={styles.growField}>
				<ApplinksAuthSelect name={props.name} defaultValue={getAuthOptionFromConfig()} />
			</Box>
			<ApplinksConnectedArrow isIncoming={props.isIncoming} state={props.state} />
		</Inline>
	);
};

const RemoteAuthDetails = (props: {
	type: TypeDefinition;
	oauthConfig?: RestOAuthConfig;
	url: string;
}) => {
	const getAuthDescription = useCallback(() => {
		return getAuthLevel(props.oauthConfig).label;
	}, [props.oauthConfig]);

	return (
		<Inline space="space.300">
			<Inline space="space.100">
				{props.type.icon}
				<Link appearance="subtle" href={props.url}>
					{props.type.name}
				</Link>
			</Inline>
			<Text>{getAuthDescription()}</Text>
		</Inline>
	);
};

const ConfigureAuthTable = (props: {
	applink: ApplicationLinkV3Data;
	localAuth?: RestApplinkOAuthStatus;
	remoteAuth?: RestApplinkOAuthStatus;
}) => {
	const formState = useFormState<OAuth1EditForm>({
		values: true,
	});

	const getApplinkType = useCallback(() => {
		return lookupTypeFromV3Data(props.applink);
	}, [props.applink]);

	const getOutgoingMatchState = useCallback(() => {
		return matchOAuthConfigs(
			formState?.values.outgoingAuth,
			getAuthLevel(props.remoteAuth?.incoming),
		);
	}, [props.remoteAuth, formState?.values.outgoingAuth]);

	const getIncomingMatchState = useCallback(() => {
		return matchOAuthConfigs(
			formState?.values.incomingAuth,
			getAuthLevel(props.remoteAuth?.outgoing),
		);
	}, [props.remoteAuth, formState?.values.incomingAuth]);

	return (
		<table>
			<colgroup>
				<col css={styles.colDirection} />
				<col css={styles.colOutgoing} />
				<col css={styles.colIncoming} />
			</colgroup>
			<thead>
				<tr>
					<th>Direction</th>
					<th>Local authentication</th>
					<th>Remote authentication</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Outgoing</td>
					<td>
						<LocalAuthDetails
							isIncoming={false}
							state={getOutgoingMatchState()}
							name="outgoingAuth"
							defaultOAuthConfig={props.localAuth?.outgoing}
						/>
					</td>
					<td>
						<RemoteAuthDetails
							type={getApplinkType()}
							url={props.applink.displayUrl}
							oauthConfig={props.remoteAuth?.incoming}
						/>
					</td>
				</tr>
				<tr>
					<td>Incoming</td>
					<td>
						<LocalAuthDetails
							isIncoming
							state={getIncomingMatchState()}
							name="incomingAuth"
							defaultOAuthConfig={props.localAuth?.incoming}
						/>
					</td>
					<td>
						<RemoteAuthDetails
							type={getApplinkType()}
							url={props.applink.displayUrl}
							oauthConfig={props.remoteAuth?.outgoing}
						/>
					</td>
				</tr>
			</tbody>
		</table>
	);
};

type OAuth1EditForm = {
	displayName: string;
	rpcUrl: string;
	displayUrl: string;
	outgoingAuth: ValueType<AuthOption>;
	incomingAuth: ValueType<AuthOption>;
};

type ApplinksOAuth1EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
	status: ApplicationLinkV3Status | null;
};

const buildOAuthConfigFromOption = (option: ValueType<AuthOption>): RestOAuthConfig => {
	if (option === AUTH_OAUTH1) {
		return {
			enabled: true,
			twoLoEnabled: true,
			twoLoImpersonationEnabled: false,
			oAuth2ThreeLoEnabled: false,
		};
	}
	if (option === AUTH_OAUTH1_IMPERSONATION) {
		return {
			enabled: true,
			twoLoEnabled: true,
			twoLoImpersonationEnabled: true,
			oAuth2ThreeLoEnabled: false,
		};
	}
	return {
		enabled: false,
		twoLoEnabled: false,
		twoLoImpersonationEnabled: false,
		oAuth2ThreeLoEnabled: false,
	};
};

const ApplinksOAuth1EditPageContents = (props: ApplinksOAuth1EditPageContentsProps) => {
	const { push } = useRouterActions();

	const onFormSubmit = useCallback(
		async (data: OAuth1EditForm) => {
			const applinkUpdate: ApplicationLinkV3Simple = {
				id: props.applink.id,
				type: props.applink.type,
				system: props.applink.system,
				primary: props.applink.primary,
				isExtraAuthRequired: props.applink.isExtraAuthRequired,

				// Only change these properties:
				name: data.displayName,
				displayUrl: data.displayUrl,
				rpcUrl: data.rpcUrl,
			};

			await updateApplinkV3(props.applink.id, applinkUpdate);

			await putApplinkOAuth1Config(props.applink.id, {
				outgoing: buildOAuthConfigFromOption(data.outgoingAuth),
				incoming: buildOAuthConfigFromOption(data.incomingAuth),
			});

			push(APPLINKS_LIST_PAGE_URL);
		},
		[props, push],
	);

	return (
		<>
			<Form<OAuth1EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title="Link details">
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl
										name="rpcUrl"
										defaultUrl={props.applink.rpcUrl}
										disabled={false}
									/>
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							<FormSection title="Connections">
								{props.status ? (
									<Stack space="space.200" xcss={styles.formSectionInner}>
										<ConfigureAuthTable
											applink={props.applink}
											localAuth={props.status.localAuthentication}
											remoteAuth={props.status.remoteAuthentication}
										/>
										<Box>
											<Link
												href={getApplinksAdminUrl(props.applink.displayUrl, props.applink.type)}
												target="_blank"
											>
												Go to {props.applink.displayUrl}
											</Link>
										</Box>
									</Stack>
								) : (
									<Inline space="space.200" xcss={styles.formSectionInner}>
										<Spinner />
										<Text>Checking application link status...</Text>
									</Inline>
								)}
							</FormSection>

							<FormFooter>
								<ButtonGroup label="Form submit options">
									<LinkButton href={APPLINKS_LIST_PAGE_URL} appearance="subtle">
										Cancel
									</LinkButton>
									<Button type="submit" appearance="primary" isLoading={submitting}>
										Save
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth1EditPageContents;
